// 🔒 PROTECTED: MacTrack Diet Calculation Service
// This service uses closures and obfuscation to protect business logic

export const ProtectedDietCalculator = (() => {
  // Private constants - hidden from external access
  const _secretCoefficients = {
    // BMR calculation coefficients (Mifflin-St Jeor Equation)
    bmr: {
      male: { weight: 10, height: 6.25, age: 5, constant: 5 },
      female: { weight: 10, height: 6.25, age: 5, constant: -161 }
    },
    // Activity level multipliers
    activity: {
      sedentary: 1.2,
      lightly_active: 1.375,
      moderately_active: 1.55,
      very_active: 1.725,
      extremely_active: 1.9
    },
    // Goal adjustment factors
    goals: {
      lose_weight: 0.85, // 15% deficit
      maintain: 1.0,     // No change
      gain_muscle: 1.15  // 15% surplus
    },
    // Macro ratios for different diet types
    macroRatios: {
      balanced: { protein: 0.3, carbs: 0.4, fat: 0.3 },
      keto: { protein: 0.25, carbs: 0.05, fat: 0.7 },
      high_protein: { protein: 0.4, carbs: 0.3, fat: 0.3 },
      low_carb: { protein: 0.35, carbs: 0.15, fat: 0.5 }
    }
  };

  // Private helper functions - not accessible externally
  const _calculateBMR = (age: number, weight: number, height: number, gender: string): number => {
    const coeffs = _secretCoefficients.bmr[gender as keyof typeof _secretCoefficients.bmr];
    return coeffs.weight * weight + coeffs.height * height - coeffs.age * age + coeffs.constant;
  };

  const _calculateTDEE = (bmr: number, activityLevel: string): number => {
    const multiplier = _secretCoefficients.activity[activityLevel as keyof typeof _secretCoefficients.activity];
    return bmr * multiplier;
  };

  const _calculateTargetCalories = (tdee: number, goal: string, targetWeightChange?: number): number => {
    let baseCalories = tdee * _secretCoefficients.goals[goal as keyof typeof _secretCoefficients.goals];
    
    // Additional adjustments based on weight change target
    if (targetWeightChange && goal === 'lose_weight') {
      // 1 kg = 7700 calories, so weekly deficit calculation
      const weeklyDeficit = targetWeightChange * 7700;
      const dailyDeficit = weeklyDeficit / 7;
      // Allow deeper deficit before clamping. New floor: 60% of TDEE (was 70%).
      baseCalories = Math.max(baseCalories - dailyDeficit, tdee * 0.6);
    } else if (targetWeightChange && goal === 'gain_muscle') {
      // Additional surplus for muscle gain
      const additionalSurplus = targetWeightChange * 100; // 100 calories per kg target
      baseCalories = baseCalories + additionalSurplus;
    }
    
    return Math.round(baseCalories);
  };

  const _calculateMacros = (targetCalories: number, dietType: string, weightInKg: number): {
    proteinTarget: number;
    carbsTarget: number;
    fatTarget: number;
  } => {
    const ratios = _secretCoefficients.macroRatios[dietType as keyof typeof _secretCoefficients.macroRatios];
    const weightInLbs = weightInKg * 2.20462;

    // Calculate protein (g/lb bodyweight for high protein diets)
    let proteinTarget: number;
    if (dietType === 'high_protein') {
      proteinTarget = weightInLbs * 1.6; // 1.6g per lb
    } else if (dietType === 'keto') {
      proteinTarget = weightInLbs * 1.2; // 1.2g per lb for keto
    } else {
      proteinTarget = (targetCalories * ratios.protein) / 4; // Standard calculation
    }

    // Calculate carbs and fat
    const carbsTarget = (targetCalories * ratios.carbs) / 4;
    const fatTarget = (targetCalories * ratios.fat) / 9;

    return {
      proteinTarget: Math.round(proteinTarget),
      carbsTarget: Math.round(carbsTarget),
      fatTarget: Math.round(fatTarget)
    };
  };

  // Public interface - only exposes what's necessary
  return {
    calculateDietGoals: (params: {
      age: number;
      weight: number;
      height: number;
      gender: string;
      activityLevel: string;
      goal: string;
      dietType: string;
      targetWeightChange?: number;
    }) => {
      try {
        // Validate inputs
        if (!params.age || !params.weight || !params.height || !params.gender || !params.activityLevel || !params.goal || !params.dietType) {
          throw new Error('Missing required parameters for diet calculation');
        }

        // Calculate BMR
        const bmr = _calculateBMR(params.age, params.weight, params.height, params.gender);
        
        // Calculate TDEE
        const tdee = _calculateTDEE(bmr, params.activityLevel);
        
        // Calculate target calories
        const targetCalories = _calculateTargetCalories(tdee, params.goal, params.targetWeightChange);
        
        // Calculate macros
        const macros = _calculateMacros(targetCalories, params.dietType, params.weight);

        return {
          bmr: Math.round(bmr),
          tdee: Math.round(tdee),
          targetCalories,
          proteinTarget: macros.proteinTarget,
          carbsTarget: macros.carbsTarget,
          fatTarget: macros.fatTarget,
          calculatedAt: new Date().toISOString()
        };
      } catch (error) {
        console.error('Diet calculation error:', error);
        throw new Error('Failed to calculate diet goals');
      }
    },

    // Limited public methods for validation only
    validateInputs: (params: any): boolean => {
      return !!(params.age && params.weight && params.height && params.gender && params.activityLevel && params.goal && params.dietType);
    },

    // Get available diet types (public information)
    getAvailableDietTypes: () => {
      return Object.keys(_secretCoefficients.macroRatios);
    },

    // Get available activity levels (public information)
    getAvailableActivityLevels: () => {
      return Object.keys(_secretCoefficients.activity);
    }
  };
})();

// Type definitions for external use
export interface DietCalculationParams {
  age: number;
  weight: number;
  height: number;
  gender: string;
  activityLevel: string;
  goal: string;
  dietType: string;
  targetWeightChange?: number;
}

export interface CalculatedDietGoals {
  bmr: number;
  tdee: number;
  targetCalories: number;
  proteinTarget: number;
  carbsTarget: number;
  fatTarget: number;
  calculatedAt: string;
}
